import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useSuiteStore } from '@/stores/useSuiteStore'

// Miroir des réponses de boostcore `admin/cegid/exports` (CegidExportAdminController).

export type CegidExportBatchType = 'TIERS' | 'RIBS' | 'INVOICES'
export type CegidExportBatchStatus =
  | 'PENDING'
  | 'GENERATING'
  | 'READY'
  | 'UPLOADING'
  | 'UPLOADED'
  | 'PROCESSING'
  | 'COMPLETED'
  | 'PARTIAL'
  | 'FAILED'
export type CegidExportTrigger = 'CRON' | 'MANUAL' | 'API'
type CegidExportLineStatus = 'PENDING' | 'OK' | 'ERROR' | 'SKIPPED'

export interface CegidExportBatchRow {
  id: string
  type: CegidExportBatchType
  status: CegidExportBatchStatus
  /** Plage de factures couverte (YYYY-MM-DD) — null sur les anciens batchs. */
  dateFrom: string | null
  dateTo: string | null
  sourceCode: string | null
  trigger: CegidExportTrigger | null
  triggeredBy: string | null
  createdAt: string
  generatedAt: string | null
  filename: string | null
  hasFile: boolean
  lineCount: number
  lineStatusCounts: Record<CegidExportLineStatus, number>
}

interface CegidExportBatchLineDetail {
  id: string
  lineNumber: number
  status: CegidExportLineStatus
  errorMessage: string | null
  entityType: 'INVOICE' | 'COMPANY' | 'RIB'
  entityUuid: string
  /** Nom métier : numéro de facture ou nom de société. */
  label: string | null
  companyName: string | null
  invoiceDate: string | null
  amountTotal: string | number | null
}

export interface CegidExportBatchDetail extends CegidExportBatchRow {
  errorMetadata: Record<string, unknown> | null
  dependsOnBatchIds: string[]
  lines: CegidExportBatchLineDetail[]
}

export interface GenerateExportResult {
  period: { from: string; to: string }
  sourceCode: string
  invoicesListedInOdoo: number
  tiers: {
    newTiersDetected: number
    batchId: string | null
    filename: string | null
    lineCount: number
    list: Array<{ auxiliaire: string; libelle: string; companyUuid: string }>
  }
  invoices: {
    batchId: string | null
    filename: string | null
    lineCount: number
    exported: number
    excluded: Array<{ invoiceName: string; reason: string }>
    warnings: Array<{ invoiceName: string; messages: string[] }>
  }
  /** Présent uniquement si l'envoi par email a été demandé. */
  email?: { sent: boolean; recipients: string[]; error?: string }
}

export const useCegidExportsAdminStore = defineStore('cegidExportsAdmin', () => {
  const suiteStore = useSuiteStore()

  const batches = ref<CegidExportBatchRow[]>([])
  const isLoadingBatches = ref(false)
  const isGenerating = ref(false)

  async function fetchBatches(opts: { limit?: number } = {}): Promise<CegidExportBatchRow[]> {
    isLoadingBatches.value = true
    try {
      const qs = new URLSearchParams()
      if (opts.limit) qs.set('limit', String(opts.limit))
      const suffix = qs.toString() ? `?${qs.toString()}` : ''
      const res = await suiteStore.suiteFetch(`admin/cegid/exports${suffix}`, {
        method: 'GET',
        credentials: 'include',
      })
      if (res && typeof res !== 'boolean' && res.ok) {
        batches.value = ((await res.json()) as CegidExportBatchRow[]) ?? []
        return batches.value
      }
    } catch (e) {
      console.error('Erreur récupération exports CEGID', e)
    } finally {
      isLoadingBatches.value = false
    }
    return []
  }

  async function fetchDetail(batchId: string): Promise<CegidExportBatchDetail | null> {
    const res = await suiteStore.suiteFetch(`admin/cegid/exports/${batchId}`, {
      method: 'GET',
      credentials: 'include',
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      return (await res.json()) as CegidExportBatchDetail
    }
    return null
  }

  async function generate(
    dateFrom: string,
    dateTo: string,
    forceReexport = false,
    sendEmail = false,
  ): Promise<{ ok: boolean; data?: GenerateExportResult; message?: string }> {
    isGenerating.value = true
    try {
      const res = await suiteStore.suiteFetch('admin/cegid/exports/generate', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dateFrom, dateTo, forceReexport, sendEmail }),
      })
      if (res && typeof res !== 'boolean' && res.ok) {
        return { ok: true, data: (await res.json()) as GenerateExportResult }
      }
      let message = 'Échec de la génération'
      if (res && typeof res !== 'boolean') {
        try {
          const b = await res.json()
          if (b?.message) message = Array.isArray(b.message) ? b.message.join(', ') : String(b.message)
        } catch {
          // ignore
        }
      }
      return { ok: false, message }
    } catch (e) {
      return { ok: false, message: (e as Error).message }
    } finally {
      isGenerating.value = false
    }
  }

  /** Télécharge le .tra d'un batch (blob → lien temporaire). */
  async function downloadFile(batchId: string, filename?: string | null): Promise<boolean> {
    const res = await suiteStore.suiteFetch(`admin/cegid/exports/${batchId}/download`, {
      method: 'GET',
      credentials: 'include',
    })
    if (!res || typeof res === 'boolean' || !res.ok) return false
    const blob = await res.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename || `${batchId}.tra`
    a.click()
    URL.revokeObjectURL(url)
    return true
  }

  return {
    batches,
    isLoadingBatches,
    isGenerating,
    fetchBatches,
    fetchDetail,
    generate,
    downloadFile,
  }
})
